<template>
    <div class="container card">
        <FullscreenLoading v-if="isLoading" />

        <div class="d-flex justify-content-between align-items-center">
            <h1>Companies</h1>
            <div @click="showCreateForm = true" style="cursor: pointer;">
                <button class="btn btn-secondary btn-sm rounded-circle btn-icon-only float-right" v-tooltip="'Toevoegen'">
                    <icon name="plus"></icon>
                </button>
            </div>
        </div>
        <table v-if="companiesData?.data?.length > 0" class="table table-striped table-hover">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Naam</th>
                    <th>Hostname</th>
                    <th>Plechtigheden</th>
                    <th>Gebruikers</th>
                    <ordering
                        @change="getCompanies"
                        v-model="order"
                        field="referred_by"
                        tag="th"
                    >
                        Verwezen door
                    </ordering>
                    <ordering
                        @change="getCompanies"
                        v-model="order"
                        field="last_ceremony_created_at"
                        tag="th"
                        class="text-right"
                    >
                        Laatste aangemaakte plechtigheid op
                    </ordering>
                    <ordering
                        @change="getCompanies"
                        v-model="order"
                        field="created_at"
                        tag="th"
                        class="text-right"
                    >
                        Aangemaakt op
                    </ordering>
                    <th class="text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="company in companiesData.data" :key="company.name">
                    <td>{{ company.id }}</td>
                    <td>
                        {{ company.name }}
                        <span v-if="company.deleted_at" class="badge badge-danger" v-tooltip="'Dit bedrijf is verwijderd'">
                            <icon name="exclamation" scale="0.8" class="m-0" style="fill: #fff" />
                        </span>
                    </td>
                    <td>{{ company.hostname }}</td>
                    <td>{{ company.total_ceremonies }}</td>
                    <td>{{ company.total_users }}</td>
                    <td>{{ company.referred_by ? company.referred_by : '-' }}</td>
                    <td class="text-right">{{ company.last_ceremony_created_at ? formatDateTime(company.last_ceremony_created_at) : '-' }}</td>
                    <td class="text-right">{{ formatDateTime(company.created_at) }}</td>
                    <td class="text-right">
                        <button v-if="!company.deleted_at" class="btn btn-xs rounded-pill btn-primary"
                                @click="editCompany(company)" v-tooltip="'Bewerken'">
                            <icon name="pencil-alt" scale="0.8"></icon>
                        </button>
                        <button v-if="!company.deleted_at" class="btn btn-xs rounded-pill btn-danger"
                                @click="deleteCompany(company)" v-tooltip="'Verwijderen'">
                            <icon name="trash" scale="0.8"></icon>
                        </button>
                        <router-link
                            v-if="!company.deleted_at && company.total_users && hasPermission('adminImpersonateUser')"
                            tag="button"
                            class="btn btn-xs rounded-pill btn-warning"
                            :to="{ name: 'admin-users-overview', query: { search: company.name } }"
                            v-tooltip="'Gebruikers overnemen'"
                        >
                            <icon name="user-secret" scale="0.8"></icon>
                        </router-link>
                        <button class="btn btn-xs rounded-pill btn-info"
                                @click="selectCompany(company)" v-tooltip="'Selecteer om locaties te tonen (blok hieronder)'">
                            <icon name="search" scale="0.8"></icon>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-else class="text-center alert alert-warning">
            <p class="mb-0">Geen locaties gevonden (voeg of verwijder filters toe voor resultaten).</p>
        </div>

        <Pagination :pagination="companiesData" @change="getCompanies" class="mt-2 mb-2" />
        <!-- Delete Confirmation Modal -->
        <modal :show="showDeleteModal" @close="closeDeleteModal">
            <template #header>
                <div class="modal-header">
                    <h5 class="modal-title">Delete Company</h5>
                    <button type="button" class="close" @click="closeDeleteModal">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <template #body>
                <div class="modal-body">
                    Are you sure you want to delete this company?
                </div>
            </template>
            <template #footer>
                <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
                <button type="button" class="btn btn-danger" @click="confirmDeleteCompany">Delete</button>
            </template>
        </modal>
        <!-- Edit Company Modal -->
        <modal :show="showEditForm" @close="closeEditForm" custom_content_css="width:1000px;" custom_dialog_css="justify-content: center; display:grid">
            <template #header>
                <div class="modal-header">
                    <h5 class="modal-title">Edit Company</h5>
                    <button type="button" class="close" @click="closeEditForm">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <template #body>
                <div class="modal-body">
                    <form @submit.prevent="updateCompany" class="needs-validation" novalidate>
                        <div class="row ">
                            <div v-for="(value, key, index) in selectedCompany" :key="key" class="form-group col-md-6 pr-5" v-if="!notShownFields.includes(key)">
                                <label :for="key" class="form-label">{{ key }}
                                    <icon name="question-circle" v-tooltip="getTooltip(key)" />
                                    <span v-if="isRequiredIndication(key)" class="text-danger">*</span>
                                </label>
                                <div v-if="(typeof value === 'boolean')">
                                    <div class="custom-control custom-switch">
                                        <input type="checkbox" class="custom-control-input" :id="key" v-model="selectedCompany[key]">
                                        <label class="custom-control-label" :for="key"></label>
                                    </div>
                                </div>
                                <div v-else-if="key === 'block_titles'">
                                    <textarea class="form-control" :id="key" :value="JSON.stringify(selectedCompany[key]) || ''" @input="updateObjectValue(key, $event.target.value)"></textarea>
                                </div>
                                <div v-else>
                                    <input class="form-control" type="text" :id="key" v-model="selectedCompany[key]">
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </template>
            <template #footer>
                <button type="button" class="btn btn-secondary" @click="closeEditForm">Cancel</button>
                <button type="button" class="btn btn-primary" @click="updateCompany">Save</button>
            </template>
        </modal>
        <!-- Create Company Modal -->
        <modal :show="showCreateForm" @close="closeCreateForm" custom_content_css="width:1000px;" custom_dialog_css="justify-content: center; display:grid">
            <template #header>
                <div class="modal-header">
                    <h5 class="modal-title">Create Company</h5>
                    <button type="button" class="close" @click="closeCreateForm">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <template #body>
                <div class="modal-body">
                    <form @submit.prevent="createCompany" class="needs-validation" novalidate>
                        <div class="row">
                            <div v-for="(value, key, index) in newCompany" :key="key" class="form-group col-md-6 pr-5" v-if="!notShownFields.includes(key)">
                                <label :for="key" class="form-label">{{ key }}
                                    <icon name="question-circle" v-tooltip="getTooltip(key)" />
                                    <span v-if="isRequiredIndication(key)" class="text-danger">*</span>
                                </label>
                                <div v-if="typeof value === 'boolean'">
                                    <div class="custom-control custom-switch">
                                        <input type="checkbox" class="custom-control-input" :id="key" v-model="newCompany[key]">
                                        <label class="custom-control-label" :for="key"></label>
                                    </div>
                                </div>
                                <div v-else>
                                    <input class="form-control" type="text" :id="key" v-model="newCompany[key]">
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </template>
            <template #footer>
                <button type="button" class="btn btn-secondary" @click="closeCreateForm">Cancel</button>
                <button type="button" class="btn btn-primary" @click="createCompany">Create</button>
            </template>
        </modal>
    </div>
</template>

<script>
    import Modal from '../../../components/Modal.vue';
    import FullscreenLoading from '../../../components/FullscreenLoading.vue';
    import Pagination from '../../../components/Pagination.vue';
    import Ordering from '../../../components/Ordering.vue';
    import UtilMixin from '../../mixins/UtilMixin.vue';
    import { mapGetters } from 'vuex';

    export default {
        components: { FullscreenLoading, Modal, Pagination, Ordering },

        mixins: [
            UtilMixin,
        ],

        props: {
            companies: {
                type: Object,
                default: () => {
                },
            },
            ordering: {
                type: String,
                required: true,
            },
        },

        watch: {
            companies: {
                immediate: true,
                deep: true,
                handler(newValue) {
                    this.companiesData = newValue;
                },
            },
            ordering: {
                handler(newValue) {
                    this.order = newValue;
                },
            },
        },

        computed: {
            booleanToNumber: {
                get() {
                    let result = {};
                    for (let key in this.selectedCompany) {
                        let value = this.selectedCompany[key];
                        if (typeof value === 'boolean') {
                            result[key] = value ? 1 : 0;
                        }
                    }
                    return result;
                },
                set(newValue) {
                    for (let key in newValue) {
                        this.selectedCompany[key] = newValue[key] === 1;
                    }
                },
            },

            ...mapGetters(['hasPermission'])
        },

        data() {
            return {
                isLoading: false,

                companiesData: {},
                selectedCompany: null,
                showEditForm: false,
                showDeleteModal: false,
                order: this.ordering,
                notShownFields: ['created_at', 'updated_at', 'deleted_at', 'id', 'total_ceremonies', 'total_users'],
                newCompany: {
                    'name': '',
                    'hash': '',
                    'hostname': '',
                    'websocket_hostname': '',
                    'submit_info': '',
                    'manual_info': '',
                    'mail_template': '',
                    'mail_from_address': '',
                    'mail_from_name': '',
                    'primary_color': '',
                    'fixed_block_enabled': false,
                    'play_without_transition': false,
                    'can_repeat_block': false,
                    'back_confirmation': false,
                    'show_history': false,
                    'play_translation': '',
                    'default_fade_out_time': '',
                    'set_duration_enabled': false,
                    'set_duration_devices': '',
                    'set_duration_confirmation_enabled': false,
                    'custom_css': '',
                    'auto_submit_ceremonies_before_hours': '',
                    'external_service_warning_email': '',
                    'call_enabled': false,
                    'call_streaming_server': '',
                    'advanced_background_enabled': false,
                    'no_block_selected_text': '',
                    'show_fixed_block_after_speaker_finish': false,
                    'block_titles': '',
                    'tracker_id': '',
                    'music_only_block_disabled': false,
                    'skip_repeat_for_duration_count': false,
                    'call_streaming_server_username': '',
                    'call_streaming_server_password': '',
                    'can_book_type_ledwall': false,
                    'ignore_inloop_ceremony_total_duration': true,
                    'image_blur_amount': 20,
                    'fixed_blocks_draggable': false,
                },
                showCreateForm: false,
            };
        },

        methods: {
            updateObjectValue(key, value) {
                try {
                    this.selectedCompany[key] = JSON.parse(value);
                } catch (error) {
                    console.error('Invalid JSON format');
                }
            },
            // Tooltip
            getTooltip(key) {
                const tooltips = {
                    'id': 'Het ID van de company',
                    'name': 'De naam van de klant',
                    'hash': 'Een unieke md5 hash van de naam van de klant',
                    'hostname': 'De hostname van de omgeving: demo.draaiboek-online.nu',
                    'websocket_hostname': 'The websocket hostname van de klant. demo-ws.draaiboek-online.nu',
                    'submit_info': 'De informatie die getoond wordt bij het indienen van een plechtigheid',
                    'manual_info': 'Stukje tekst wat zichtbaar is bij het klikken op de handleiding',
                    'mail_template': 'Standaard mail template. kopieren van andere omgeving en telefoonnummer etc aanpassen',
                    'mail_from_address': 'Standaard email adres no-reply@draaiboek-online.nu',
                    'mail_from_name': 'Naam van de klant',
                    'created_at': 'The creation date of the company.',
                    'updated_at': 'The last update date of the company.',
                    'deleted_at': 'The deletion date of the company.',
                    'primary_color': 'The primary color of the company. #c1c1c1c bijv',
                    'fixed_block_enabled': 'Instellingen of het vaste blok aan staat. (Je moet nieuwe plechtigheid aanmaken om deze te zien)',
                    'play_without_transition': 'In de controller een knop tonen waarbij je een blok kan starten zonder overgang',
                    'can_repeat_block': 'In de controller een knop tonen waarbij je een blok kan herhalen',
                    'back_confirmation': 'In de controller om bevestiging vragen indien je op terug klikt',
                    'show_history': 'In de controller bij overzicht historie van plechtigheden tonen',
                    'play_translation': 'De vertaling van de tekst op de knop om af te spelen.',
                    'default_fade_out_time': 'Standaard fadeout tijd. bijv 5000ms',
                    'set_duration_enabled': 'Je moet de set duration in de tijdbalk eerst unlocken voordat je het kan slepen, anders kan je het niet slepen',
                    'set_duration_devices': 'The set duration devices of the company.',
                    'set_duration_confirmation_enabled': 'Whether the set duration confirmation is enabled for the company.',
                    'custom_css': 'Custom css toevoegen voor deze klant',
                    'auto_submit_ceremonies_before_hours': 'Het aantal uur voor de plechtigheid dat de plechtigheid automatisch wordt ingediend',
                    'external_service_warning_email': 'Email adres waar de waarschuwingen naar toe gestuurd worden als bijvoorbeeld het aanmaken van een stream niet goed gaat.',
                    'call_enabled': 'Instellingen of het remote inbellen aanstaat',
                    'call_streaming_server': 'De streaming server van het remote inbellen. possum.302.nl:1990',
                    'advanced_background_enabled': 'Instellingen waarbij je achtergrond instellingen kan maken op een blok.',
                    'no_block_selected_text': 'Hulptekst onder de standaard twee blokken indien er nog geen blok is geselecteerd',
                    'show_fixed_block_after_speaker_finish': '??? Hou maar op 0',
                    'block_titles': 'Aangepast titels voor inloop blok en uitloop blok: {"inloop":"Vaste foto ontvangkamer","uitloop":"foto koffiekamer"}',
                    'tracker_id': 'The tracker ID of the company.(reachd tracker.)',
                    'music_only_block_disabled': 'Instellingen bij aanmaken van een blok waarbij de optie alleen muziek toevoegen weg gaat. (Enkel muziek is geen foto uploaden)',
                    'skip_repeat_for_duration_count': 'Voor de totale duur van een uitvaartplechtigheid worden bij herhalende blokken alleen de totale duur van de nummers meegeteld en niet de repeated duration,',
                    'call_streaming_server_username': 'Username van de streaming server van het remote inbellen.',
                    'call_streaming_server_password': 'Password van de streaming server van het remote inbellen.',
                    'image_blur_amount': 'Hoeveelheid van de blur van de afbeelding in de controller.',
                    'fixed_blocks_draggable': 'Instellingen of de vaste blokken in de ontwerper te slepen zijn.',
                };
                return tooltips[key] || '';
            },

            //     Delete company
            closeDeleteModal() {
                this.showDeleteModal = false;
            },
            async confirmDeleteCompany() {
                this.isLoading = true;

                const companyName = this.selectedCompany.name;
                try {
                    await this.$http.delete(`/api/backend/companies/${this.selectedCompany.id}`);
                    this.companiesData = this.companiesData.data.filter(company => company.id !== this.selectedCompany.id);
                    this.selectedCompany = null;
                    this.showDeleteModal = false;
                    this.$notify({
                        group: 'default',
                        title: 'Bedrijf verwijdert',
                        text: `Het bedrijf ${companyName} is successvol verwijdert`,
                        type: 'success',
                        duration: 2000,
                        position: 'top center',
                    });
                } catch (error) {
                    console.error(error);
                    this.$notify({
                        group: 'default',
                        title: 'Bedrijf verwijdert',
                        text: `Het bedrijf ${companyName} is niet verwijdert`,
                        type: 'error',
                        duration: 2000,
                        position: 'top center',
                    });
                }

                this.isLoading = false;
            },
            deleteCompany(company) {
                this.selectedCompany = { ...company };
                this.showDeleteModal = true;
            },

            // Edit company
            editCompany(company) {
                this.selectedCompany = { ...company };
                if (this.selectedCompany.deleted_at) {
                    let dateParts = this.selectedCompany.deleted_at.split(' ');
                    this.selectedCompany.deleted_at = dateParts[0];
                }
                this.showEditForm = true;
            },
            closeEditForm() {
                this.showEditForm = false;
            },
            async updateCompany() {
                this.isLoading = true;

                const companyName = this.selectedCompany.name;
                try {
                    await this.$http.put(`/api/backend/companies/${this.selectedCompany.id}`, this.selectedCompany);
                    const index = this.companiesData.data.findIndex(company => company.id === this.selectedCompany.id);
                    this.companiesData.data[index] = { ...this.selectedCompany };
                    this.selectedCompany = null;
                    this.showEditForm = false;
                    this.$notify({
                        group: 'default',
                        title: 'Bedrijf aangepast',
                        text: `Het bedrijf ${companyName} is successvol aangepast`,
                        type: 'success',
                        duration: 2000,
                        position: 'top center',
                    });
                } catch (error) {
                    console.error(error);
                    this.$notify({
                        group: 'default',
                        title: 'Bedrijf aangepast',
                        text: `Het bedrijf ${companyName} is niet aangepast`,
                        type: 'error',
                        duration: 2000,
                        position: 'top center',
                    });
                }

                this.isLoading = false;
            },

            // Create company
            isRequiredIndication(key) {
                const requiredFields = ['name', 'hash', 'hostname', 'websocket_hostname', 'mail_from_address', 'mail_from_name', 'set_duration_enabled', 'back_confirmation', 'block_titles', 'skip_repeat_for_duration_count'];
                return requiredFields.includes(key);
            },
            closeCreateForm() {
                this.showCreateForm = false;
            },
            getCompanies() {
                this.$emit('getCompanies', { page: this.companiesData.current_page, order: this.order });
            },
            async createCompany() {
                this.isLoading = true;

                try {
                    const response = await this.$http.post('/api/backend/companies/', this.newCompany);
                    this.companiesData.data.push(response.data);
                    this.newCompany = {}; // Reset the form
                    this.showCreateForm = false;
                    this.getCompanies();
                } catch (error) {
                    this.$notify({
                        group: 'default',
                        title: 'Bedrijf aanmaken',
                        text: `${error.response.data.message}`,
                        type: 'error',
                        duration: 8000,
                        position: 'top center',
                    });
                    console.error(error);
                }

                this.isLoading = false;
            },

            selectCompany(selectedCompany) {
                this.$emit('company-selected', selectedCompany.id);
            },
        },

        mounted() {
            this.getCompanies();
        },
    };
</script>
